import React, { useState } from "react";
import { Box, Tabs, Tab, Container, Paper, IconButton, useTheme, Typography, alpha } from "@mui/material";
import InstructorAuthForm from "../../components/auth/InstructorAuthForm";
import StudentAuthForm from "../../components/auth/StudentAuthForm";
import { useThemeContext } from "../../../context/ThemeContext";
import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import { ReactComponent as Logo } from "../../../assets/logo.svg";

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`auth-tabpanel-${index}`}
            aria-labelledby={`auth-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 4 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

export default function AuthPage() {
    const [value, setValue] = useState(0);
    const { mode, toggleTheme } = useThemeContext();
    const theme = useTheme();

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    return (
        <Box
            sx={{
                height: '100vh',
                bgcolor: 'background.default',
                position: 'relative',
                overflowX: 'hidden',
                overflowY: 'auto',
                pt: { xs: 12, md: 20 }, // Fixed top padding to position content consistently
                pb: 4
            }}
        >
            {/* Background Decoration */}
            <Box
                sx={{
                    position: 'absolute',
                    top: { xs: -50, md: -100 },
                    right: { xs: -50, md: -100 },
                    width: { xs: 250, md: 400 },
                    height: { xs: 250, md: 400 },
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.05),
                    zIndex: 0
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: { xs: -25, md: -50 },
                    left: { xs: -25, md: -50 },
                    width: { xs: 200, md: 300 },
                    height: { xs: 200, md: 300 },
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    zIndex: 0
                }}
            />

            <Box sx={{ position: 'absolute', top: 24, right: 24, zIndex: 1 }}>
                <IconButton onClick={toggleTheme} color="default">
                    {mode === "dark" ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
            </Box>

            <Container component="main" maxWidth="sm" sx={{ zIndex: 1, position: 'relative' }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: '24px',
                        overflow: 'hidden',
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: (theme) => `0 8px 40px ${alpha(theme.palette.primary.main, 0.08)}`, // Enhanced shadow matching theme
                        transition: 'height 0.3s ease'
                    }}
                >
                    <Box sx={{
                        pt: 5,
                        pb: 4,
                        px: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <Logo style={{ height: 48, width: 48 }} />
                            <Typography component="h1" variant="h4" fontWeight="800" color="primary.main" sx={{ letterSpacing: '-0.5px' }}>
                                Assignify
                            </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" align="center">
                            Sign in to manage your courses
                        </Typography>
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={value}
                            onChange={handleChange}
                            aria-label="authentication tabs"
                            variant="fullWidth"
                            textColor="primary"
                            indicatorColor="primary"
                            sx={{
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 600,
                                    fontSize: '1rem',
                                    py: 2
                                }
                            }}
                        >
                            <Tab label="Instructor" id="auth-tab-0" aria-controls="auth-tabpanel-0" />
                            <Tab label="Student" id="auth-tab-1" aria-controls="auth-tabpanel-1" />
                        </Tabs>
                    </Box>
                    <TabPanel value={value} index={0}>
                        <InstructorAuthForm />
                    </TabPanel>
                    <TabPanel value={value} index={1}>
                        <StudentAuthForm />
                    </TabPanel>
                </Paper>
            </Container>
        </Box>
    );
}
